(() => {
  if (customElements.get('image-slider')) return;

  class ImageSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.image-slider__track');
      this.slides = Array.from(this.querySelectorAll('.image-slide'));

      if (!this.track || this.slides.length === 0) return;

      this.controls = this.querySelector('[data-slider-controls]');
      this.previousButton = this.querySelector('.slider-arrow-prev');
      this.nextButton = this.querySelector('.slider-arrow-next');
      this.dots = Array.from(this.querySelectorAll('[data-slide-index]'));
      this.status = this.querySelector('[data-slider-status]');
      this.autoplayButton = this.querySelector('[data-autoplay-toggle]');
      this.loop = this.dataset.loop === 'true';
      this.autoplayConfigured = this.dataset.autoplay === 'true';
      this.autoplaySpeed = Math.max(Number(this.dataset.autoplaySpeed) || 5000, 1000);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayIntent = this.autoplayConfigured && !this.motionQuery.matches;
      this.isHovered = false;
      this.hasFocusWithin = false;
      this.isInViewport = false;
      this.currentIndex = 0;
      this.maxIndex = 0;
      this.visibleSlides = 1;
      this.announceNextChange = false;
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            this.announceNextChange = true;
            this.goTo(Number(dot.dataset.slideIndex));
          },
          { signal }
        );
      });

      this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.track.addEventListener(
        'pointerdown',
        () => {
          this.announceNextChange = true;
          this.restartAutoplay();
        },
        { passive: true, signal }
      );
      this.track.addEventListener(
        'wheel',
        () => {
          this.announceNextChange = true;
          this.restartAutoplay();
        },
        { passive: true, signal }
      );

      this.addEventListener(
        'mouseenter',
        () => {
          this.isHovered = true;
          this.syncAutoplay();
        },
        { signal }
      );
      this.addEventListener(
        'mouseleave',
        () => {
          this.isHovered = false;
          this.syncAutoplay();
        },
        { signal }
      );
      this.addEventListener(
        'focusin',
        () => {
          this.hasFocusWithin = true;
          this.syncAutoplay();
        },
        { signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.hasFocusWithin = false;
          this.syncAutoplay();
        },
        { signal }
      );

      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

      this.onMotionPreferenceChange = () => {
        if (this.motionQuery.matches) this.autoplayIntent = false;
        this.updateAutoplayButton();
        this.syncAutoplay();
        this.syncAutoplayVideos();
      };
      this.motionQuery.addEventListener('change', this.onMotionPreferenceChange);

      this.resizeObserver = new ResizeObserver(() => this.updateMetrics());
      this.resizeObserver.observe(this.track);

      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = entry.isIntersecting;
          this.syncAutoplay();
          this.syncAutoplayVideos();
        },
        { threshold: 0.2 }
      );
      this.viewportObserver.observe(this);

      this.setupAutoplayVideos();
      this.classList.add('is-enhanced');
      requestAnimationFrame(() => this.updateMetrics());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.viewportObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.motionQuery?.removeEventListener('change', this.onMotionPreferenceChange);
      clearTimeout(this.autoplayTimer);
      cancelAnimationFrame(this.updateFrame);
    }

    updateMetrics() {
      if (!this.track || !this.slides.length) return;

      const firstSlide = this.slides[0];
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap) || 0;
      const stride = firstSlide.getBoundingClientRect().width + gap;

      if (!this.track.clientWidth || !stride) {
        if (this.controls) this.controls.hidden = true;
        return;
      }

      this.visibleSlides = Math.max(1, Math.min(this.slides.length, Math.round((this.track.clientWidth + gap) / stride)));
      this.maxIndex = Math.max(0, this.slides.length - this.visibleSlides);
      this.hasOverflow = this.maxIndex > 0 && this.track.scrollWidth > this.track.clientWidth + 1;
      this.currentIndex = Math.min(this.getNearestIndex(), this.maxIndex);

      if (this.controls) this.controls.hidden = !this.hasOverflow;

      this.dots.forEach((dot, index) => {
        dot.hidden = !this.hasOverflow || index > this.maxIndex;
      });

      this.updateUI(false);
      this.updateAutoplayButton();
      this.syncAutoplay();
    }

    requestUpdate() {
      if (this.updateFrame) return;

      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        const nextIndex = Math.min(this.getNearestIndex(), this.maxIndex);
        const changed = nextIndex !== this.currentIndex;
        this.currentIndex = nextIndex;
        this.updateUI(changed && this.announceNextChange);

        if (changed) this.restartAutoplay();
      });
    }

    getNearestIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const isRtl = getComputedStyle(this.track).direction === 'rtl';
      const trackStart = isRtl ? trackRect.right : trackRect.left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideStart = isRtl ? slideRect.right : slideRect.left;
        const distance = Math.abs(slideStart - trackStart);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    move(step) {
      if (!this.hasOverflow) return;

      let targetIndex = this.currentIndex + step;

      if (this.loop) {
        if (targetIndex < 0) targetIndex = this.maxIndex;
        if (targetIndex > this.maxIndex) targetIndex = 0;
      } else {
        targetIndex = Math.max(0, Math.min(targetIndex, this.maxIndex));
      }

      this.announceNextChange = true;
      this.goTo(targetIndex);
    }

    goTo(index, behavior = this.motionQuery.matches ? 'auto' : 'smooth') {
      const targetIndex = Math.max(0, Math.min(index, this.maxIndex));
      const targetSlide = this.slides[targetIndex];
      if (!targetSlide) return;

      const trackRect = this.track.getBoundingClientRect();
      const targetRect = targetSlide.getBoundingClientRect();
      const isRtl = getComputedStyle(this.track).direction === 'rtl';
      const distance = isRtl ? targetRect.right - trackRect.right : targetRect.left - trackRect.left;

      this.track.scrollBy({ left: distance, behavior });
      this.currentIndex = targetIndex;
      this.updateUI(this.announceNextChange);
      this.restartAutoplay();
    }

    onKeydown(event) {
      if (
        event.target !== this.track &&
        event.target.closest('a, button, input, select, textarea, video, [contenteditable="true"]')
      ) {
        return;
      }

      const isRtl = getComputedStyle(this.track).direction === 'rtl';
      let targetIndex;

      if (event.key === 'ArrowRight') targetIndex = this.currentIndex + (isRtl ? -1 : 1);
      if (event.key === 'ArrowLeft') targetIndex = this.currentIndex + (isRtl ? 1 : -1);
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.maxIndex;
      if (targetIndex === undefined) return;

      event.preventDefault();
      this.announceNextChange = true;
      this.goTo(targetIndex);
    }

    onBlockSelect(event) {
      const selectedSlide = event.target.closest?.('.image-slide');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const selectedIndex = this.slides.indexOf(selectedSlide);
      this.autoplayIntent = false;
      this.updateAutoplayButton();
      this.goTo(Math.min(selectedIndex, this.maxIndex), 'auto');
    }

    updateUI(announce = false) {
      if (this.previousButton) {
        this.previousButton.disabled = !this.hasOverflow || (!this.loop && this.currentIndex === 0);
      }
      if (this.nextButton) {
        this.nextButton.disabled = !this.hasOverflow || (!this.loop && this.currentIndex === this.maxIndex);
      }

      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex && index <= this.maxIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (announce && this.status) {
        this.status.textContent = `${this.currentIndex + 1} ${this.dataset.ofLabel || '/'} ${this.maxIndex + 1}`;
        this.announceNextChange = false;
      }
    }

    toggleAutoplay() {
      this.autoplayIntent = !this.autoplayIntent;
      this.updateAutoplayButton();
      this.syncAutoplay();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;

      const isUnavailable = !this.hasOverflow || this.motionQuery.matches;
      this.autoplayButton.hidden = isUnavailable;
      this.autoplayButton.dataset.paused = String(!this.autoplayIntent);
      this.autoplayButton.setAttribute(
        'aria-label',
        this.autoplayIntent ? this.autoplayButton.dataset.labelPause : this.autoplayButton.dataset.labelPlay
      );
    }

    shouldAutoplay() {
      return (
        this.autoplayIntent &&
        this.hasOverflow &&
        !this.motionQuery.matches &&
        !document.hidden &&
        this.isInViewport &&
        !this.isHovered &&
        !this.hasFocusWithin
      );
    }

    syncAutoplay() {
      clearTimeout(this.autoplayTimer);
      if (!this.shouldAutoplay()) return;

      this.autoplayTimer = window.setTimeout(() => {
        const targetIndex = this.currentIndex >= this.maxIndex ? 0 : this.currentIndex + 1;
        this.goTo(targetIndex);
      }, this.autoplaySpeed);
    }

    restartAutoplay() {
      if (!this.autoplayIntent) return;
      this.syncAutoplay();
    }

    onVisibilityChange() {
      this.syncAutoplay();
      this.syncAutoplayVideos();
    }

    setupAutoplayVideos() {
      this.autoplayVideos = Array.from(this.querySelectorAll('video[data-video-autoplay="true"]'));
      if (!this.autoplayVideos.length) return;

      this.visibleAutoplayVideos = new Set();
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.visibleAutoplayVideos.add(entry.target);
            } else {
              this.visibleAutoplayVideos.delete(entry.target);
            }
          });
          this.syncAutoplayVideos();
        },
        { root: this.track, threshold: [0, 0.6] }
      );

      this.autoplayVideos.forEach((video) => this.videoObserver.observe(video));
    }

    syncAutoplayVideos() {
      this.autoplayVideos?.forEach((video) => {
        const canPlay =
          this.visibleAutoplayVideos?.has(video) &&
          this.isInViewport &&
          !document.hidden &&
          !this.motionQuery.matches;

        if (canPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  }

  customElements.define('image-slider', ImageSlider);
})();
